// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/** The row cap. Above this the tool refuses rather than freezing a phone. */
export const MAX_ROWS = 25_000;

export type Role = 'corpus' | 'eval';

export interface ColumnProfile {
  name: string;
  index: number;
  /** Rows sampled that were non-empty. */
  filled: number;
  /** Distinct values seen in the sample. */
  distinct: number;
  meanLength: number;
  maxLength: number;
  /** Mean words per non-empty value. */
  meanWords: number;
  /** Higher is more likely to be the free-text column. */
  textScore: number;
  samples: string[];
}

export interface LoadedFile {
  role: Role;
  name: string;
  bytes: number;
  format: 'csv' | 'jsonl';
  delimiter: string;
  headers: string[];
  columns: ColumnProfile[];
  proposedColumn: number;
  /** Total records read, before the cap. */
  totalRows: number;
  truncated: boolean;
}

export interface ClusterView {
  id: number;
  label: string;
  /** User-supplied name, which wins over `label` everywhere including exports. */
  renamed: string | null;
  size: number;
  share: number;
  /** Mean cosine of members to the centroid. */
  cohesion: number;
  /** Share of members closer to some other centroid's boundary than a margin. */
  ambiguous: number;
  terms: { term: string; score: number }[];
  exemplars: { row: number; text: string; similarity: number }[];
  covered: boolean;
  coveringCount: number;
  nearestCount: number;
  bestEvalSimilarity: number;
  radius: number;
}

export interface Headline {
  k: number;
  corpusRows: number;
  evalRows: number;
  uncoveredVolume: number;
  chanceUncoveredVolume: number;
  uncoveredClusters: number;
  chanceUncoveredClusters: number;
  detectionFloor: number;
  outOfRangeEvalRows: number;
  emptyRows: number;
}

export interface AnalysisResult {
  clusters: ClusterView[];
  headline: Headline;
  seed: number;
}

/** Messages the worker sends back. Every long step reports determinate progress. */
export type WorkerOut =
  | { type: 'log'; level: 'info' | 'good' | 'warn' | 'bad'; message: string }
  | { type: 'model-progress'; received: number; total: number; fromCache: boolean; label: string }
  | { type: 'model-ready'; vocabSize: number; dims: number; tokens: number }
  | { type: 'profiled'; file: LoadedFile }
  | { type: 'progress'; stage: string; done: number; total: number; rate?: number }
  | { type: 'suggested-k'; k: number; fittedOn: number }
  | { type: 'result'; result: AnalysisResult }
  | { type: 'export'; kind: ExportKind; blob: Blob; filename: string }
  | { type: 'error'; message: string; stage: string };

export type ExportKind = 'labelled' | 'topics' | 'sample';

export type WorkerIn =
  | { type: 'load-model' }
  | { type: 'profile'; role: Role; blob: Blob; name: string }
  | { type: 'analyse'; corpusColumn: number; evalColumn: number | null; k: number; seed: number }
  | { type: 'suggest-k' }
  | { type: 'rename'; cluster: number; name: string | null }
  | { type: 'export'; kind: ExportKind }
  | { type: 'reset' };
